import { Box, ButtonBase, createStyles, makeStyles, Theme, Typography, useMediaQuery } from '@material-ui/core';
import cn from 'classnames';
import * as React from 'react';
import { getSpace } from '../../content/getSpace/getSpace';
import { SpaceContext } from '../../content/types/SpaceContext';
import useDictionary from '../../dictionaries/context/useDictionary';
import RenderText from '../../dictionaries/RenderText';
import { BankType, NegotiationOption, NegotiationOptionAlternative, NegotiationType, OutDebt } from '../../generated/graphql';
import { isCypressTest } from '../../isCypressTest';
import { getExtraN26Suffix } from '../../utils/getExtraN26Suffix';
import { getMainOutDebtFromParent } from '../../utils/getMainOutDebtFromParent';
import { getNegBooleans } from '../../utils/getNegBooleans';
import Collapse from '../Collapse/Collapse';
import AccordionNegOptionsAcpkChild from './AccordionNegOptionsAcpkChild';
import AccordionNegOptionsCardTitle from './AccordionNegOptionsCardTitle';
import AccordionNegOptionsTitle from './AccordionNegOptionsTitle';
import ExternalNegotiationInstructions from './ExternalNegotiationInstructions';
import NewPaymentTermsWithAlternative from './NewPaymentTerms/NewPaymentTermsWithAlternatives';
import { getBankLabel } from '../../utils/getBankLabel';

export interface AccordionNegOptionsProps {
  negOptions: NegotiationOption[];
  onPreConfirmAlternative: () => void;
  outDebt: OutDebt;
  outDebts: OutDebt[];
  selectedOption: NegotiationOption | null;
  setSelectedOption: React.Dispatch<React.SetStateAction<NegotiationOption | null>>;
  selectedAlternative: NegotiationOptionAlternative | null;
  setSelectedAlternative: React.Dispatch<React.SetStateAction<NegotiationOptionAlternative | null>>;
  className?: string;
}

const AccordionNegOptions: React.FC<AccordionNegOptionsProps> = (props) => {
  const {
    negOptions,
    onPreConfirmAlternative,
    outDebt, outDebts, selectedOption,
    setSelectedAlternative,
    setSelectedOption,
    selectedAlternative, className,
  } = props;

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles();
  const { gT, dictionary } = useDictionary();
  const extra = { gT, dictionary };

  // popupRate for banco bogota balance behind payment low rate
  const popupRate = outDebt?.extra?.popupRate || '';

  const handleChangeOption = ({ type }: Pick<NegotiationOption, 'type'>) => {
    if (negOptions.length > 1) {
      setSelectedAlternative(null);
      const option = negOptions.find((opt) => opt.type === type);
      setSelectedOption((prev) => (!option || prev?.type === type ? null : option));
    }
  };

  const onHandleChangeShowOption = (option: NegotiationOption) => () => {
    handleChangeOption(option);
  };

  const isWeb = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  React.useEffect(() => {
    // in web we don't do the scroll because the options should always be visible
    // and in cypress tests the scroll creates problems
    if (isWeb || isCypressTest) return undefined;
    const timer = setTimeout(() => {
      if (!!selectedOption) {
        const btn = document.querySelector(`[data-test="cy-newPaymentTermsBtn-selected-${selectedOption?.type}-${selectedAlternative?.term}"]`);
        btn?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [ selectedAlternative, selectedOption, isWeb ]);

  return (
    <Box className={cn(classes.root, className)} data-test="cy-accordionNegOption">
      <AccordionNegOptionsTitle outDebt={outDebt} negOptions={negOptions} />
      {
        negOptions.map((option: NegotiationOption, index) => {
          const { type, alternatives, settings } = option;
          const isSelected = selectedOption?.type === type;
          const withSeveralOptions = negOptions.length > 1;
          const ParentComponent = withSeveralOptions ? ButtonBase : Box;
          const alternative = alternatives.find(
            ({ term, type: altType }) => term === selectedAlternative?.term
            && altType === selectedAlternative?.type,
          );
          const [ firstAlternative ] = alternatives;

          const negBooleans = getNegBooleans(outDebt, option, alternative || firstAlternative);
          const { isVirtualDebt } = negBooleans;
          const { parentOptionDebt, isShortcutToParent } = settings || {};
          const parentDebt = outDebts.find((od) => od.id === parentOptionDebt);
          const showShortcut = parentDebt && isShortcutToParent;
          const n26Suffix = getExtraN26Suffix(outDebt, option);
          const isBogotaBalanceBehindPayment = outDebt?.bank === BankType.BancoDeBogota
          && option.type === NegotiationType.BalanceBehindPayment;
          const isBogotaRefinance = outDebt?.bank === BankType.BancoDeBogota
          && option.type === NegotiationType.Refinance;
          const selectedBdbAlternative = alternative || firstAlternative;
          const bdbSubType = (isBogotaBalanceBehindPayment || isBogotaRefinance)
            && selectedBdbAlternative?.type
            ? `_${selectedBdbAlternative.type}`
            : '';
          const descriptionTextKey = `negotiation_${type}${isVirtualDebt || isShortcutToParent ? 'VIRTUAL' : ''}${n26Suffix}_description${bdbSubType}`;
          const { productNumber } = getMainOutDebtFromParent(outDebt);
          const descriptionCtx = {
            bank: outDebt.bank,
            bankLabel: getBankLabel(outDebt.bank),
            productNumber: `**** ****${productNumber}`,
            popupRate,
            issuingEntity: outDebt.extra?.issuingEntity || '',

          };

          // space implementation
          const spaceContext: SpaceContext = {
            bank: outDebt.bank,
            bankLabel: getBankLabel(outDebt.bank),
            brand: outDebt.brand!,
            country: outDebt.country!,
            outDebt,
            option,
            alternative: alternative || firstAlternative,
            negBooleans,

          };
          const descriptionSpace = getSpace('NEG_OPTION', 'description', spaceContext, extra);
          const showExternalInstructions = getSpace('NEG_OPTION_EXTERNAL_INSTRUCTIONS', 'title', spaceContext, extra).exists;

          return (
            <Box
              className={cn(
                classes.item,
                index === negOptions.length - 1 && isSelected && classes.itemWithoutShadow,
              )}
              key={type}
              id={type}
              data-testid="accordion-neg-options__neg-option"
            >
              <ParentComponent
                className={classes.itemHeader}
                onClick={onHandleChangeShowOption(option)}
              >
                <AccordionNegOptionsCardTitle
                  negOption={option}
                  alternative={alternative || firstAlternative}
                  isSelected={isSelected}
                  withExpandButton={withSeveralOptions}
                  outDebt={outDebt}
                />
                {(descriptionSpace.exists || !!gT(descriptionTextKey)) && (
                  <Typography
                    className={classes.itemHeaderDescription}
                    data-test="cy-negOption-description"
                  >
                    <RenderText
                      textKey={descriptionSpace.keyIf || descriptionTextKey}
                      isMarkdown
                      context={{ ...descriptionCtx, ...descriptionSpace.extraContext }}
                    />
                  </Typography>
                )}
              </ParentComponent>
              <Collapse in={isSelected}>
                {showExternalInstructions && (
                  <ExternalNegotiationInstructions
                    debt={outDebt}
                    negOption={option}
                    alternative={alternative || firstAlternative}
                  />
                )}
                {!showExternalInstructions && showShortcut && parentDebt && (
                  <AccordionNegOptionsAcpkChild parentDebt={parentDebt} />
                )}
                {!showExternalInstructions && !showShortcut && (
                  <NewPaymentTermsWithAlternative
                    debt={outDebt}
                    isSelected={isSelected}
                    selectedOption={option}
                    selectedAlternative={alternative || null}
                    onPreConfirmAlternative={onPreConfirmAlternative}
                    setSelectedAlternative={setSelectedAlternative}
                    onChangeSelectedOption={handleChangeOption}
                  />
                )}
              </Collapse>
            </Box>
          );
        })
      }
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) => createStyles({
  root: {
    borderRadius: theme.spacing(0.5),
    margin: theme.spacing(0, -3),
    [theme.breakpoints.up('sm')]: {
      margin: theme.spacing(0),
    },
  },
  item: {
    boxShadow: theme.shadows[5],
    borderBottom: `solid 1px ${theme.palette.indigo.main}40`,
    borderRadius: theme.spacing(0.5),
    overflow: 'hidden',
    [theme.breakpoints.up('sm')]: {
      border: `solid 1px ${theme.palette.indigo.main}40`,
      marginBottom: theme.spacing(2),
    },
  },
  itemWithoutShadow: {
    [theme.breakpoints.down('xs')]: {
      boxShadow: theme.shadows[0],
      borderBottom: 'solid 1px transparent',
      marginBottom: theme.spacing(-5),
    },
  },
  itemHeader: {
    padding: theme.spacing(2),
    boxSizing: 'border-box',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  itemHeaderDescription: {
    ...theme.typography.small as any,
    color: theme.palette.text3.main,
    textAlign: 'left',
    whiteSpace: 'pre-line',
  },
}));

export default AccordionNegOptions;
